import {
  Injectable,
  inject,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { Database, ref, objectVal } from "@angular/fire/database";
import { Observable, combineLatest, interval, of } from "rxjs";
import { map, shareReplay, startWith, switchMap } from "rxjs/operators";
import { UtilService } from "./util.service";

export interface RunningClock {
  levelNum: number;
  sb: number | null;
  bb: number | null;
  ante: number | null;
  kBlinds: { sb: any; bb: any; ante?: any };
  timeRemaining: number;
  timeText: string;
  isBreak: boolean;
  running: boolean;
}

interface LiveState {
  started?: number | null;
  seconds?: number;
  running?: boolean;
}

interface Level {
  sb?: number;
  bb?: number;
  ante?: number;
  levelTime: number;
  afterLevel?: number;
  num?: number;
}

@Injectable()
export class RunningClockService {
  private tick$ = interval(1000).pipe(
    startWith(0),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly mdb = inject(Database);
  private readonly injector = inject(EnvironmentInjector);

  constructor(private util: UtilService) {}

  watch(tournID: string): Observable<RunningClock | null> {
    const live$ = runInInjectionContext(this.injector, () =>
      objectVal<LiveState>(ref(this.mdb, `tournaments/${tournID}/live`))
    );
    const structure$ = runInInjectionContext(this.injector, () =>
      objectVal<any>(ref(this.mdb, `structures/${tournID}/levels`))
    );

    return combineLatest([live$, structure$]).pipe(
      switchMap(([live, levelsRaw]) => {
        const levels = this.normalizeLevels(levelsRaw);
        if (!live || !levels || !levels.length) {
          return of<RunningClock | null>(null);
        }
        return this.tick$.pipe(map(() => this.compute(live, levels)));
      })
    );
  }

  private normalizeLevels(raw: any): Level[] | null {
    if (!raw) return null;
    let flat: Level[] = [];
    if (Array.isArray(raw)) flat = raw.filter((l) => l);
    else if (typeof raw === "object") {
      flat = Object.keys(raw)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => raw[k])
        .filter((l) => l);
    }
    if (!flat.length) return null;
    return this.buildPlayOrder(flat);
  }

  // Mirrors TournamentService.buildStructure(): sort blind levels by
  // blind value and interleave breaks at their afterLevel positions.
  private buildPlayOrder(levels: Level[]): Level[] {
    const blinds: Level[] = [];
    const breaks: Level[] = [];
    const seen = new Set<number>();
    for (const l of levels) {
      if (l.sb) blinds.push(l);
      else if (l.afterLevel! >= 0 && !seen.has(l.afterLevel!)) {
        breaks.push(l);
        seen.add(l.afterLevel!);
      }
    }
    blinds.sort((a, b) => {
      const ia = a.sb! + a.bb! / 10 + (a.ante ? a.ante / 100 : 0);
      const ib = b.sb! + b.bb! / 10 + (b.ante ? b.ante / 100 : 0);
      return ia - ib;
    });
    breaks.sort((a, b) => a.afterLevel! - b.afterLevel!);
    const ordered: Level[] = [];
    let nextBreak = breaks.shift();
    let num = 0;
    for (const blevel of blinds) {
      if (nextBreak && nextBreak.afterLevel === num) {
        ordered.push({ ...nextBreak });
        nextBreak = breaks.shift();
      }
      num++;
      ordered.push({ ...blevel, num });
    }
    return ordered;
  }

  private compute(live: LiveState, levels: Level[]): RunningClock | null {
    const baseSeconds = live.seconds ?? 0;
    const elapsed =
      live.running && live.started
        ? Math.max(0, (Date.now() - live.started) / 1000)
        : 0;
    let sec = baseSeconds + elapsed;

    let lvl = 0;
    let level = levels[lvl];
    while (sec >= level.levelTime * 60 && lvl < levels.length - 1) {
      sec -= level.levelTime * 60;
      lvl++;
      level = levels[lvl];
    }
    if (sec >= level.levelTime * 60) sec = level.levelTime * 60;

    const timeRemaining = Math.max(
      0,
      Math.floor(level.levelTime * 60 - sec)
    );
    const isBreak = level.sb == null;
    const kBlinds = isBreak
      ? { sb: null, bb: null }
      : this.util.kBlinds({
          sb: level.sb,
          bb: level.bb,
          ante: level.ante,
        });

    return {
      levelNum: level.num ?? lvl + 1,
      sb: level.sb ?? null,
      bb: level.bb ?? null,
      ante: level.ante ?? null,
      kBlinds,
      timeRemaining,
      timeText: this.util.timeText(timeRemaining),
      isBreak,
      running: !!live.running,
    };
  }
}
